/**
 * Validation framework
 */
import moment from 'moment';

export interface Validator<TValue> {
  /** If a string is returned it represents a validation error */
  (value: TValue): string | null | undefined;
}

export function applyValidators<TValue>(value: TValue, validators: Validator<TValue>[]): string | null {
  for (let i = 0; i < validators.length; i++) {
    const res = validators[i](value);
    if (res) return res;
  }
  return null;
}

/**
 * - FalsyValues/{}/Whitespace strings are considered invalid
 */
export const required: Validator<any> = (value: any) => {
  if (!value
    || (!Object.keys(value).length && !(value instanceof Date)) // empty objects but not Date
    || (typeof value === 'string' && !value.trim())) {
    return "Value Required";
  }
  return null;
}

export const isChecked: Validator<any> = (value: any) => {
  if (!value) {
    return "Please accept the Terms and Conditions and privacy statement before proceeding";
  }
  return null;
}

export const minLength = (minValue: number): Validator<string> => {
  return (value: string) => {
    value = value.trim();

    if (value.length < minValue) {
      return 'Length must be more than ' + minValue.toString();
    }
    return null;
  }
}

export const maxLength = (maxValue: number): Validator<string> => {
  return (value: string) => {
    value = value.trim();

    if (value.length > maxValue) {
      return 'Length must be less than ' + maxValue.toString();
    }
    return null;
  }
}

export const pattern = (message: string, pattern: RegExp): Validator<string> => {
  return (value: string) => {
    value = value.trim();

    if (!value.match(pattern)) {
      return message;
    }
    return null;
  }
}

export const email: Validator<string> = (value: string) => {
  if (value == null || value == '') return null;
  if (typeof value !== 'string') {
    return "Email must be a string";
  }
  value = value.trim();
  // Src : http://stackoverflow.com/a/719543/390330
  if (!/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/g.exec(value)) {
    return "Not a valid email address";
  }
  return null;
}

export const dateTime: Validator<string> = (value: string) => {
  if (value == null) return null;
  const invalid = `Invalid date time format: ${Dates.dateFormats.dateTimeFormat}`;
  let {valid, date} = Dates.toDate(value, [Dates.dateFormats.dateOnlyFormat, Dates.dateFormats.dateTimeFormat]);
  if (!valid) {
    return invalid;
  }
  return null;
}

export const dateOnly: Validator<string> = (value: string) => {
  if (value == null) return null;
  const invalid = `Invalid date format: ${Dates.dateFormats.dateOnlyFormat}`;
  let {valid, date} = Dates.toDate(value, [Dates.dateFormats.dateOnlyFormat]);
  if (!valid) {
    return invalid;
  }
  return null;
}

export const minDate = (date: Date): Validator<Date> => {
  let display = moment(date).format('DD/MM/YYYY');
  return (value: Date) => {
    if (value == null) return null;
    /** if you have a date. It better be after */
    if (moment(value).isAfter(date)) {
      return null;
    }
    return `Date must be after ${display}`;
  };
}

export const maxDate = (date: Date): Validator<Date> => {
  let display = moment(date).format('DD/MM/YYYY');
  return (value: Date) => {
    if (value == null) return null;
    /** if you have a date. It better be before */
    if (moment(value).isBefore(date)) {
      return null;
    }
    return `Date must be before ${display}`;
  };
}

export const hourInFuture: Validator<Date> = (value: Date) => {
  if (value == null) return null;
  // close to an hour in the future
  var minimum = moment(new Date());
  minimum.add('minutes', 55);

  if (moment(value).isBefore(minimum)) {
    return 'Value must be at least one hour in the future';
  }
  return null;
}

export const int: Validator<number> = (value: number) => {
  if (value == null) return null;
  if (value !== parseInt(value.toString(), 10)) {
    return 'Not an integer';
  }
  return null;
}

export const minInt = (minValue: number): Validator<number> => {
  return (value: number) => {
    if (value == null) return null;
    if (!int(value)) return int(value);

    if (value < minValue) {
      return 'Not before ' + minValue.toString();
    }
    return null;
  }
}

export const maxInt = (maxValue: number): Validator<number> => {
  return (value: number) => {
    if (value == null) return null;
    if (!int(value)) return int(value);

    if (value > maxValue) {
      return 'Not before ' + maxValue.toString();
    }
    return null;
  }
}

export namespace Dates {

  export const dateFormats = {
    dateTimeFormat: 'DD/MM/YYYY HH:mm',
    dateOnlyFormat: 'DD/MM/YYYY',
    isoFormat: 'YYYY-MM-DD HH:mm:SS ', // Corresponds to '2016-01-04T13:00:00Z'
  }

  export function toDate(value: string,
    formats = [dateFormats.dateTimeFormat, dateFormats.dateOnlyFormat, dateFormats.isoFormat]
  ): { valid: boolean, date: Date } {
    if (!value || !value.trim().length) {
      return { valid: true, date: null };
    }
    let trimmed = value.trim();
    if (!formats.some(format => format.length == trimmed.length)) {
      return { valid: false, date: null };
    }
    // http://momentjs.com/docs/#/parsing/string-formats/
    let date = moment(value, formats, true);
    if (!date.isValid()) {
      return { valid: false, date: null };
    }
    return { valid: true, date: date.toDate() };
  }
}
